import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  FormControlLabel,
  ButtonGroup,
  Button,
  TextField,
  Divider,
  Chip as MuiChip,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from '@mui/material';
import TextField2 from '@mui/material/TextField';
import { Autocomplete } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewListIcon from '@mui/icons-material/ViewList';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CustomPaper from '../common/CustomPaper';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import FormMentionEditor from '../Component/FormMentionEditor';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import checkKey from '../../utils/CheckKey';
import useAIGeneratedFormStore, { getAIGeneratedFormStore } from './useAIGeneratedFormStore';
import '../FormBuilder/FormDialogs/FormDialogs.css';

const PLACEHOLDER_FIELDS = [
  { stateKey: 'formIdKey', label: 'Form ID', outputField: 'formId' },
  { stateKey: 'formSessionIdKey', label: 'Form Session ID', outputField: 'formSessionId' },
  { stateKey: 'formSessionURLKey', label: 'Form Session URL', outputField: 'formSessionURL' },
  { stateKey: 'dataKey', label: 'Data', outputField: 'data' },
  { stateKey: 'answersKey', label: 'Answers', outputField: 'answers' },
  { stateKey: 'questionAnswersKey', label: 'Question Answers', outputField: 'questionAnswers' },
  { stateKey: 'questionAnswersHTMLKey', label: 'Question Answers HTML', outputField: 'questionAnswersHTML' },
];

const AIGeneratedFormTemplate = (props) => {
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [linkedComponent, setLinkedComponent] = useState(false);
  const [linkedComponents, setLinkedComponents] = useState([]);
  const [relatedSequentialForm, setRelatedSequentialForm] = useState([]);
  const [redirectToExternalUrl, setRedirectToExternalUrl] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [submMsg, setSubmMsg] = useState('Thank you for your submission. Your response has been recorded.');
  const [canEditSubmission, setCanEditSubmission] = useState(false);
  const [canReadOnly, setCanReadOnly] = useState(false);
  const [canAddSubmission, setCanAddSubmission] = useState(false);
  const [canSaveSubmission, setCanSaveSubmission] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [canSubmitSubmission, setCanSubmitSubmission] = useState(true);
  const [displayMode, setDisplayMode] = useState('classic');
  const [formIdKey, setFormIdKey] = useState('');
  const [formSessionIdKey, setFormSessionIdKey] = useState('');
  const [formSessionURLKey, setFormSessionURLKey] = useState('');
  const [dataKey, setDataKey] = useState('');
  const [answersKey, setAnswersKey] = useState('');
  const [questionAnswersKey, setQuestionAnswersKey] = useState('');
  const [questionAnswersHTMLKey, setQuestionAnswersHTMLKey] = useState('');
  const [placeholderErrors, setPlaceholderErrors] = useState({});

  const store = useMemo(() => getAIGeneratedFormStore(props.component_id), [props.component_id]);

  const stateSetters = {
    formIdKey: setFormIdKey,
    formSessionIdKey: setFormSessionIdKey,
    formSessionURLKey: setFormSessionURLKey,
    dataKey: setDataKey,
    answersKey: setAnswersKey,
    questionAnswersKey: setQuestionAnswersKey,
    questionAnswersHTMLKey: setQuestionAnswersHTMLKey,
  };

  const stateGetters = {
    formIdKey,
    formSessionIdKey,
    formSessionURLKey,
    dataKey,
    answersKey,
    questionAnswersKey,
    questionAnswersHTMLKey,
  };

  const fetchRelatedForms = (savedLinkedComponentIds) => {
    send_request(
      'project-service/project/find_related_sequential_forms/' + props.component_id,
      '',
      {}
    ).then((resp) => {
      if (resp && resp.data) {
        const keys = Object.keys(resp.data);
        const formsList = keys.map((k) => ({
          label:
            resp.data[k].componentName && resp.data[k].componentName !== ''
              ? resp.data[k].componentName
              : k,
          componentId: k,
        }));

        let lc = [];
        if (savedLinkedComponentIds) {
          for (let a = 0; a < savedLinkedComponentIds.length; a++) {
            const id = savedLinkedComponentIds[a];
            if (resp.data[id]) {
              lc.push({
                label:
                  resp.data[id].componentName && resp.data[id].componentName !== ''
                    ? resp.data[id].componentName
                    : id,
                componentId: id,
              });
            }
          }
        }

        setRelatedSequentialForm(formsList);
        setLinkedComponents(lc);
      }
    });
  };

  const handleEditPlaceholder = (stateKey, event) => {
    const placeholderValue = replacePlaceholderValue(event.target.value);
    const validation = checkKey(placeholderValue, null, []);
    stateSetters[stateKey](placeholderValue);
    setPlaceholderErrors((prev) => ({
      ...prev,
      [stateKey]: validation[0] ? validation[1] : '',
    }));
  };

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      if (relatedSequentialForm.length === 0) {
        fetchRelatedForms(linkedComponents.map((lc) => lc.componentId));
      }

      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    const loadData = async () => {
      try {
        const response = await send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );

        const component = response?.data?.components?.[0] || response?.data;
        const rData = component?.componentData?.data || {};

        const existingPrompt =
          rData.prompt != null ? rData.prompt.toString() : '';

        const defaults = store.getState().getTemplate().data;
        setPrompt(existingPrompt);
        setLinkedComponent(rData.linkedComponent ? true : false);
        setLinkedComponents(rData.linkedComponents || []);
        setRedirectToExternalUrl(rData.redirectToExternalUrl ? true : false);
        setRedirectUrl(rData.redirectUrl || '');
        setSubmMsg(rData.submMsg || 'Thank you for your submission. Your response has been recorded.');
        setCanEditSubmission(rData.canEditSubmission || false);
        setCanReadOnly(rData.canReadOnly || false);
        setCanAddSubmission(rData.canAddSubmission || false);
        setCanSaveSubmission(rData.canSaveSubmission || false);
        setAutoSave(rData.autoSave || false);
        setCanSubmitSubmission(rData.canSubmitSubmission !== false);
        setDisplayMode(rData.displayMode || 'classic');
        setFormIdKey(rData.formIdKey || defaults.formIdKey);
        setFormSessionIdKey(rData.formSessionIdKey || defaults.formSessionIdKey);
        setFormSessionURLKey(rData.formSessionURLKey || defaults.formSessionURLKey);
        setDataKey(rData.dataKey || defaults.dataKey);
        setAnswersKey(rData.answersKey || defaults.answersKey);
        setQuestionAnswersKey(rData.questionAnswersKey || defaults.questionAnswersKey);
        setQuestionAnswersHTMLKey(rData.questionAnswersHTMLKey || defaults.questionAnswersHTMLKey);

        if (component?.componentData?.name) {
          store.getState().setName(component.componentData.name);
          props.onComponentNameChanged(component.componentData.name);
        }
        if (component?.description !== undefined) {
          props.onComponentDescriptionChanged(component.description);
        }
        if (component?.componentData?.lastModified) {
          props.onLastModifiedChanged(component.componentData.lastModified);
        }

        store.setState({ loaded: true });

        fetchRelatedForms(rData.linkedComponents);

        props.setChildTemplateLoaded(true);
      } catch (e) {
        console.error('Failed to load ai_generated_form component data', e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <IndividualComponentLoader
        isAIEditing={props.isAIEditing}
        componentType={props.componentType}
        componentName={props.componentName}
        componentDescription={props.componentDescription}
      />
    );
  }

  const redirectFormOptionsExcludingSelf = relatedSequentialForm.filter(
    (f) => f.componentId !== props.component_id
  );

  const getSubmissionAction = () => {
    if (redirectToExternalUrl) return 'externalUrl';
    if (linkedComponent) return 'linkedForm';
    return 'submission';
  };

  const handleSubmissionActionChange = (value) => {
    if (value === 'submission') {
      setLinkedComponent(false);
      setRedirectToExternalUrl(false);
    } else if (value === 'linkedForm') {
      setLinkedComponent(true);
      setRedirectToExternalUrl(false);
    } else if (value === 'externalUrl') {
      setLinkedComponent(false);
      setRedirectToExternalUrl(true);
    }
  };

  return (
    <Grid container spacing={2} style={{ padding: 8 }}>
      <Grid item xs={12}>
        <CustomPaper>
          <Typography variant="h6" style={{ marginBottom: 8 }}>
            AI-generated form prompt
          </Typography>
          <Typography variant="body2" style={{ opacity: 0.8, marginBottom: 12 }}>
            Describe the form to generate
          </Typography>
          <FormMentionEditor
            aiAssistant={true}
            text={prompt}
            placeholder={'E.g. Collect a name, email, and 3 bullet points about the issue...'}
            availablePlaceholders={props.availablePlaceholders}
            onChange={(val) => setPrompt(val)}
            style={{ minHeight: 48 }}
          />
        </CustomPaper>
      </Grid>

      <Grid item xs={12}>
        <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={500}>Form settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p className="stand-alone-text">Form display mode</p>
            <p className="s-text">Choose how questions are displayed to users</p>
            <ToggleButtonGroup
              value={displayMode || 'classic'}
              exclusive
              onChange={(e, value) => value && setDisplayMode(value)}
              className="form-display-mode-selector"
            >
              <ToggleButton value="classic" className="form-display-mode-btn">
                <ViewListIcon />
                <Box className="form-display-mode-text">
                  <span className="form-display-mode-label">Classic</span>
                  <span className="form-display-mode-desc">All questions on page</span>
                </Box>
              </ToggleButton>
              <ToggleButton value="conversational" className="form-display-mode-btn">
                <ChatBubbleOutlineIcon />
                <Box className="form-display-mode-text">
                  <span className="form-display-mode-label">Conversational</span>
                  <span className="form-display-mode-desc">One question at a time</span>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider sx={{ my: 2 }} />

            <p className="stand-alone-text">After this form is submitted</p>
            <p className="s-text">Select what happens when this form is submitted</p>
            <RadioGroup
              value={getSubmissionAction()}
              onChange={(e) => handleSubmissionActionChange(e.target.value)}
            >
              <FormControlLabel value="submission" control={<Radio />} label="display submission page" />
              <FormControlLabel value="linkedForm" control={<Radio />} label="redirect to another form" />
              <FormControlLabel value="externalUrl" control={<Radio />} label="redirect to external URL" />
            </RadioGroup>

            {linkedComponent && (
              <Autocomplete
                style={{ marginTop: 8 }}
                options={redirectFormOptionsExcludingSelf}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <MuiChip key={index} className="form-chip" label={option.label} {...getTagProps({ index })} />
                  ))
                }
                size="small"
                multiple
                freeSolo={false}
                openOnFocus
                getOptionLabel={(option) => option.label}
                value={linkedComponents}
                blurOnSelect={false}
                className="form-autocomplete"
                onChange={(event, inputValue, reason) => {
                  if (reason === 'clear') {
                    setLinkedComponents([]);
                  } else {
                    setLinkedComponents(inputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField2
                    {...params}
                    placeholder="Select a connected task or form section"
                    size="small"
                    variant="outlined"
                    error={linkedComponents.length === 0}
                    FormHelperTextProps={{ className: 'form-helper-text' }}
                    helperText={
                      linkedComponents.length === 0 &&
                      'Select a form to redirect to when this form is submitted'
                    }
                  />
                )}
              />
            )}

            {redirectToExternalUrl && (
              <TextField2
                style={{ marginTop: 8 }}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="https://example.com"
                value={redirectUrl || ''}
                onChange={(e) => setRedirectUrl(e.target.value)}
              />
            )}

            {!linkedComponent && !redirectToExternalUrl && (
              <>
                <p className="primary-text">Submission Message</p>
                <p className="s-text">Specify the message displayed after a submission has been made.</p>
                <TextField
                  variant="outlined"
                  multiline
                  fullWidth
                  rows={3}
                  onChange={(e) => setSubmMsg(e.target.value)}
                  value={submMsg}
                  placeholder="Type in a message to be displayed when the user submits the form"
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <p className="stand-alone-text">Allow the user to view, edit and resubmit their response</p>
            <ButtonGroup>
              <Button
                className="d-button"
                color={canEditSubmission && !canReadOnly ? 'primary' : 'info'}
                variant={canEditSubmission && !canReadOnly ? 'contained' : 'outlined'}
                onClick={() => { setCanEditSubmission(true); setCanReadOnly(false); }}
              >
                On
              </Button>
              <Button
                className="d-btn-read-only"
                color={canReadOnly ? 'primary' : 'info'}
                variant={canReadOnly ? 'contained' : 'outlined'}
                onClick={() => { setCanEditSubmission(false); setCanReadOnly(true); }}
              >
                Read Only
              </Button>
              <Button
                className="d-button"
                color={!canEditSubmission && !canReadOnly ? 'primary' : 'info'}
                variant={!canEditSubmission && !canReadOnly ? 'contained' : 'outlined'}
                onClick={() => { setCanEditSubmission(false); setCanReadOnly(false); }}
              >
                Off
              </Button>
            </ButtonGroup>

            <p className="stand-alone-text">Allow user to save their response</p>
            <ButtonGroup>
              <Button
                className="d-button"
                color={canSaveSubmission ? 'primary' : 'info'}
                variant={canSaveSubmission ? 'contained' : 'outlined'}
                onClick={() => setCanSaveSubmission(true)}
              >
                On
              </Button>
              <Button
                className="d-button"
                color={canSaveSubmission ? 'info' : 'primary'}
                variant={canSaveSubmission ? 'outlined' : 'contained'}
                onClick={() => { setCanSaveSubmission(false); setAutoSave(false); }}
              >
                Off
              </Button>
            </ButtonGroup>

            <p className="stand-alone-text">Allow user to submit their response</p>
            <ButtonGroup>
              <Button
                className="d-button"
                color={canSubmitSubmission ? 'primary' : 'info'}
                variant={canSubmitSubmission ? 'contained' : 'outlined'}
                onClick={() => setCanSubmitSubmission(true)}
              >
                On
              </Button>
              <Button
                className="d-button"
                color={canSubmitSubmission ? 'info' : 'primary'}
                variant={canSubmitSubmission ? 'outlined' : 'contained'}
                onClick={() => setCanSubmitSubmission(false)}
              >
                Off
              </Button>
            </ButtonGroup>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={500}>Output placeholders</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" style={{ opacity: 0.8, marginBottom: 12 }}>
              Configure the placeholder names for this component's outputs.
            </Typography>
            {PLACEHOLDER_FIELDS.map((field) => (
              <Grid container spacing={1} alignItems="center" key={field.stateKey} style={{ marginBottom: 8 }}>
                <Grid item xs={4}>
                  <Typography variant="body2">{field.label}</Typography>
                </Grid>
                <Grid item xs={8}>
                  <W86PlaceholderTextField
                    value={stateGetters[field.stateKey]}
                    error={!!placeholderErrors[field.stateKey]}
                    helperText={placeholderErrors[field.stateKey] || ''}
                    placeholderText={`Enter placeholder for ${field.label.toLowerCase()}`}
                    handleChange={(e) => handleEditPlaceholder(field.stateKey, e)}
                  />
                </Grid>
              </Grid>
            ))}
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default AIGeneratedFormTemplate;
